"use client";

import React, { Fragment } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import {
  customRichTextComponent,
  richTextComponent,
} from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";
import logo from "@/public/logo-black.svg";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { PageAffiliateWorkshopSchema } from "@/sanity/pages/PageAffiliateWorkshopTemplate";
import { cn } from "@/lib/utils";
import Countdown from "@/components/global/countdown/Countdown";
import Link from "next/link";
import arrow from "@/public/global/dotted-arrow.svg";
import { PageAssetsSchema } from "@/sanity/pages/PageAssetsTemplate";

export default function What({ data }: { data: PageAssetsSchema }) {
  const bgCards: { bgColor: string }[] =
    data.pageStyle === "crypto"
      ? [{ bgColor: "#93BCDB" }, { bgColor: "#85D0A6" }, { bgColor: "#FFF7E2" }]
      : [
          { bgColor: "#FFF7E2" },
          { bgColor: "#93BCDB" },
          { bgColor: "#85D0A6" },
          { bgColor: "#93BCDB" },
        ];
  return (
    <section id="what" className="w-full h-full lg:px-[5rem]">
      <div className="relative mx-auto w-[min(230rem,100%)] bg-[#07171B] lg:rounded-[5.5rem] py-[8rem] lg:py-[12.625rem]">
        <div className="relative mx-auto w-full z-[1] flex flex-col items-start lg:items-center px-[3rem] lg:px-0">
          {data?.whatChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <ChipSerif
                text={data.whatChip}
                className="text-white mb-[1.5rem]"
              />
            </motion.div>
          )}

          <motion.div
            className="richtext richtext-start lg:richtext-center prose-headings:w-full
            prose-headings:text-white [&_span]:text-white [&_span]:font-instrument-sans [&_span]:font-semibold lg:[&_span]:font-bold lg:[&_span]:text-[#6DA991] lg:[&_span]:font-gestura
            prose-p:text-white/70 prose-p:text-[2.25rem] lg:prose-p:text-[2rem] prose-p:w-[min(46.5rem,95%)] prose-strong:text-white [text-wrap-style:pretty]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {data?.whatText && (
              <PortableText
                value={data?.whatText}
                components={richTextComponent}
              />
            )}
          </motion.div>

          {data.whatList && (
            <div className="w-[min(155.875rem,100%)] flex flex-col gap-[1.5rem] mt-[3rem] lg:mt-[4rem]">
              {data.whatList.map((item, index) => (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  key={"whatItem" + index}
                  className="rounded-[3rem] flex"
                  style={{
                    backgroundColor: bgCards[index % bgCards.length].bgColor,
                  }}
                >
                  <Image
                    src={item.image}
                    alt="image"
                    className="!w-[8rem] lg:!w-[40rem] shrink-0 rounded-[3rem] hidden lg:block"
                  />
                  <div className="p-[2rem] lg:p-[5rem] flex flex-col gap-[2rem] lg:gap-[2.75rem] w-full">
                    <div className="flex items-center gap-[2rem]">
                      <Image
                        src={item.mobileImage}
                        alt="image"
                        className="!w-[8rem] lg:!w-[40rem] shrink-0 rounded-[1.5rem] lg:hidden"
                      />
                      <div className="flex flex-col lg:flex-row items-start lg:items-center lg:justify-between w-full">
                        <span className="text-[#103037] text-[3rem] lg:text-[4rem] font-semibold tracking-[-0.09rem] lg:tracking-[-0.12rem]">
                          {item.name}
                        </span>
                        <span className="text-[#103037] text-[2rem] lg:text-[2.5rem] uppercase">
                          {item.description}
                        </span>
                      </div>
                    </div>
                    <hr className="w-full border-t border-black/20" />
                    <div className="flex flex-wrap gap-[1rem]">
                      {item.items.map((option, indexI) => (
                        <div
                          key={"what" + index + "option" + indexI}
                          className="px-[2rem] pt-[1rem] pb-[0.5rem] rounded-[0.5rem] bg-black/[0.08] text-[2rem] lg:text-[2.25rem] text-black/90 font-medium"
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
